import React, { useState, useEffect, useMemo } from 'react';
import DropdownFilter from './DropdownFilter';
import { getDynamicOptions } from '../api/report';
import { deepCompareEquals, copyWithoutEmptyArrays } from '../util/useDeepCompareWithRef';

function DynamicFilter({
	id,
	colId,
	label,
	filterKind,
	components,
	closeMenuOnSelect,
	hideSelectedOptions,
	isDisabled = false,
	isHcpName,
	filterState,
	setFilterState,
	setFilterOptions,
	isMulti,
	filterWithoutPayerDisplayName,
}) {
	const [dynamicOptions, setDynamicOptions] = useState([]);
	const [lastFilteredOptions, setLastFilteredOptions] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const setValue = (v) => {
		setFilterState({
			[id]: v,
		});
	};

	const value = filterState[id];
	const selectedOptions = useMemo(() => {
		return (value && value.map((v) => dynamicOptions.find((opt) => opt.value === v)).filter((x) => x !== undefined)) || [];
	}, [dynamicOptions, value]);

	// Create a copy of the filter excluding empty arrays since that was causing additional executions of the useEffect that fetches the options
	const cleanFilters = useMemo(() => {
		return copyWithoutEmptyArrays(filterWithoutPayerDisplayName);
	}, [filterWithoutPayerDisplayName]);

	// If our selected options don't line up with the current value, set the new selected value
	// this happens if the new list list of available options changes to not include items from our previous selection

	// this useEffect fixes bug AND bad state call error:
	useEffect(() => {
		if (value && value.length && selectedOptions.length !== value.length) {
			setFilterState((prevState) => {
				return {
					...prevState,
					[id]: selectedOptions.map((opt) => opt.value),
				};
			});
		}
	}, [value, selectedOptions, id, setFilterState]);

	useEffect(() => {
		let filteredOptions = cleanFilters;
		let ignore = false;
		if (isLoading) {
			return;
		}

		//remove the current field id from the filterWithoutPayerDisplayName so the dynamic options can be obtained without filtering by the current field (which would break multi select)
		if (Object.keys(cleanFilters).some((f) => f === id)) {
			filteredOptions = Object.assign({}, cleanFilters);
			delete filteredOptions[id];
		}
		// If the filtered options have changed, get the new dynamic options
		if (!lastFilteredOptions || !deepCompareEquals(lastFilteredOptions, filteredOptions)) {
			if (!ignore) {
				setIsLoading(true);
				getDynamicOptions(filteredOptions, colId)
					.then((options) => {
						setIsLoading(false);
						//since we are now preventing multiple requests, we need to apply the results here so the Ui is not left empty
						setDynamicOptions(options);
						setLastFilteredOptions(filteredOptions);
					})
					.catch((e) => {
						setIsLoading(false);
					});
			}
		}
		return () => {
			ignore = true;
		};
	}, [cleanFilters, colId, id, lastFilteredOptions]);

	return (
		<DropdownFilter
			label={label}
			filterKind={filterKind}
			options={dynamicOptions}
			onChange={(options) => setValue(options.map((opt) => opt.value))}
			setOptions={(v) => setFilterOptions(v)}
			value={selectedOptions}
			components={components}
			closeMenuOnSelect={closeMenuOnSelect}
			hideSelectedOptions={hideSelectedOptions}
			isDisabled={isDisabled}
			isHcpName={isHcpName}
			filterState={filterState}
			isMulti={isMulti}
		/>
	);
}

export default DynamicFilter;
